import React from 'react';
import { EventPhoto } from '../EventPhoto';
import type { IdentityWidgetProps } from '../../IdentityWidget.types';
import { useIdentityWidgetContent } from '../../useIdentityWidget';
import type { BoxProps } from '@withjoy/joykit';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { StyledDivider, StyledLinkButton, StyledSwitchEventButton, Wrapper, PlaceholderPhoto, styles } from './IdentityWidgetContent.styles';

export const IdentityWidgetContent = (props: IdentityWidgetProps & BoxProps) => {
  const { profile, eventMemberships, onSwitchEventClick, ...restProps } = props;
  const { isMobile, currentEventMembership, actionsToRender, formatDate, handleOnAddPhotosClick, identityWidgetTranslations, handleOnLogut } = useIdentityWidgetContent(props);

  const event = currentEventMembership?.event;

  const eventSwitchButton = (
    <StyledSwitchEventButton
      as="button"
      typographyVariant="label2"
      onClick={onSwitchEventClick}
      __css={styles.switchEventButton}
    >
      {event ? identityWidgetTranslations.switchEvent : identityWidgetTranslations.selectEvent}
    </StyledSwitchEventButton>
  );

  return event || actionsToRender.length ? (
    <Box
      borderRadius="inherit"
      paddingX={6}
      backgroundColor="rgba(255,255,255,0.9)"
      width="100%"
      {...restProps}
    >
      <Wrapper __css={styles.wrapper}>
        {event ? (
          <>
            <EventPhoto
              addPhotoText={identityWidgetTranslations.addPhotos}
              handleOnAddPhotosClick={handleOnAddPhotosClick}
              eventHandle={event.website}
            />
            <Flex
              flexDirection="column"
              alignItems={['flex-start', null, 'center']}
              rowGap={1}
            >
              <TextV2 typographyVariant="hed2">{event.info.eventDisplayName}</TextV2>
              {event.info.finalizedDate && (
                <TextV2
                  typographyVariant="label2"
                  color="mono12"
                >
                  {formatDate(event.info.finalizedDate)}
                </TextV2>
              )}
              {event.info.location && (
                <TextV2
                  typographyVariant="label2"
                  color="mono12"
                >
                  {event.info.location}
                </TextV2>
              )}
              {eventSwitchButton}
            </Flex>
          </>
        ) : (
          <>
            <PlaceholderPhoto __css={styles.placeholderPhoto} />
            <Flex
              flexDirection="column"
              alignItems={['flex-start', null, 'center']}
              rowGap={1}
            >
              <TextV2 typographyVariant="hed2">{identityWidgetTranslations.noEventSelected}</TextV2>
              {eventSwitchButton}
            </Flex>
          </>
        )}
      </Wrapper>

      {actionsToRender.length && (
        <Box
          as="ul"
          paddingBottom={[9, null, 5]}
        >
          {actionsToRender.map(({ label, onClick, Icon }, idx) => (
            <li key={label + 'item'}>
              <StyledLinkButton
                as="button"
                typographyVariant="hed3"
                onClick={onClick}
                __css={styles.linkButton}
              >
                <Icon />
                {label}
              </StyledLinkButton>
              {idx !== actionsToRender.length - 1 && <StyledDivider key={label + 'divider'} />}
            </li>
          ))}
        </Box>
      )}
      {profile && isMobile && (
        <ButtonV2
          onClick={handleOnLogut}
          variant="muted"
          width="100%"
        >
          {identityWidgetTranslations.signOut}
        </ButtonV2>
      )}
    </Box>
  ) : null;
};
